/**
 * Horizontal bars on a shared scale, for a hero panel: a few labelled
 * quantities compared by length, each with its figure printed past the end.
 *
 * The bars only ever draw magnitude. A negative or non-finite value draws an
 * empty fill rather than a bar running backwards. The figure is the caller's
 * own string, so this never formats or recomputes a number.
 */
import { SERIES_1, SERIES_2 } from "./chart-primitives";

export interface HBarRow {
  label: string;
  value: number;
  display: string;
  series?: 1 | 2;
}

const W = 360;
const LABEL_W = 104;
const RIGHT_W = 64;
const BAR_H = 10;

export function HBars({
  rows,
  max,
  height = 118,
  label = "Comparison",
}: {
  rows: HBarRow[];
  max?: number;
  height?: number;
  label?: string;
}) {
  if (rows.length === 0) return null;
  const drawable = (v: number) => Number.isFinite(v) && v > 0;
  const scale = max ?? Math.max(...rows.map((r) => (drawable(r.value) ? r.value : 0)));
  const trackW = W - LABEL_W - RIGHT_W;
  const rowH = (height - 12) / rows.length;

  return (
    <svg
      className="h-bars w-full max-w-[440px]"
      viewBox={`0 0 ${W} ${height}`}
      role="img"
      aria-label={label}
      preserveAspectRatio="xMidYMid meet"
    >
      {rows.map((row, i) => {
        const cy = 6 + rowH * i + rowH / 2;
        const y = cy - BAR_H / 2;
        const fill = drawable(row.value) && scale > 0 ? Math.min(row.value / scale, 1) * trackW : 0;
        return (
          <g key={row.label}>
            <text
              x={0}
              y={cy}
              dominantBaseline="central"
              fontSize={12.5}
              className="fill-muted-foreground"
            >
              {row.label}
            </text>
            <rect x={LABEL_W} y={y} width={trackW} height={BAR_H} rx={3} fill="var(--muted)" />
            <rect
              data-fill
              x={LABEL_W}
              y={y}
              width={fill}
              height={BAR_H}
              rx={3}
              fill={row.series === 2 ? SERIES_2 : SERIES_1}
            />
            <text
              x={LABEL_W + fill + 6}
              y={cy}
              dominantBaseline="central"
              fontSize={12.5}
              fontWeight={600}
              className="fill-foreground"
            >
              {row.display}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
